import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from 'app/lib/api.service';
import { COMMON_CONFIG } from 'app/lib/constants';
import { ModalService } from 'app/lib/modal.service';
import { PowerbiService } from 'app/lib/powerbi.service';
import { UserService } from 'app/lib/user.service';
import { User } from 'app/models/user';
import moment from 'moment';;
import * as powerbi from 'powerbi-client';
import { models } from 'powerbi-client';
@Component({
    selector: 'app-powerbi',
    templateUrl: './powerbi.component.html',
    styleUrls: ['./powerbi.component.scss'],
})
export class PowerbiComponent implements OnInit {
    user: User;
    @Input() orgId: number;
    private report: any;
    private tokenObject: any;
    private exportId: any;
    private reportTitle: string;
    showReport: boolean = false;
    isLoaded: boolean = false;
    showNoUserContent: boolean = false;
    showNoOrgAssociateContent: boolean = false;
    isExportInProgress = this.powerBIService.exportInProgress$;
    constructor(
        private powerBIService: PowerbiService,
        private userService: UserService,
        private modalService: ModalService,
        private translate: TranslateService
    ) {
        this.user = this.userService.getUser();
    }

    ngOnInit(): void {
        this.showNoUserContent = false;
        this.showNoOrgAssociateContent = false;
        this.embedPowerBiReport();
    }

    embedPowerBiReport() {
        this.isLoaded = false;
        const allowedRoles = ['superadmin', 'admin', 'relationshipmanager'];
        if (
            !allowedRoles.includes(this.user.userType) &&
            !this.user.primaryOrganization
        ) {
            this.isLoaded = true;
            this.showNoOrgAssociateContent = true;
            return;
        }
        const payLoad = this.orgId ? { orgreport: 1 } : {};
        this.powerBIService.getEmbedToken(payLoad).subscribe(response => {
            this.tokenObject = response;
            const embedConfig = {
                type: 'report',
                embedUrl: this.powerBIService.generateEmbedUrl(
                    this.tokenObject,
                    this.user
                ),
                accessToken: this.tokenObject?.embedToken,
                tokenType: powerbi.models.TokenType.Embed,
                viewMode: powerbi.models.ViewMode.View,
                settings: {
                    filterPaneEnabled: false,
                    navContentPaneEnabled: false,
                    hideErrors: true,
                },
                filters: this.getFilters(),
                slicers: this.getDateSlicerFilter(),
            };
            const reportContainer = document.getElementById('reportContainer');
            const powerBIClient = new powerbi.service.Service(
                powerbi.factories.hpmFactory,
                powerbi.factories.wpmpFactory,
                powerbi.factories.routerFactory
            );
            this.report = powerBIClient.embed(reportContainer, embedConfig);
            this.report.on('loaded', () => {});
            this.report.on('rendered', async () => {
                this.checkUserExistsInReport();
            });
            this.report.on('error', event => {
                let errorMessage =
                    event?.detail?.detailedMessage ||
                    this.translate.instant('error.unexpectedError');
                // let errorCode = event.detail.errorCode || 'UnknownError';
                this.modalService.showAlert(
                    this.translate.instant('error.title'),
                    errorMessage
                );
            });
        });
    }

    async checkUserExistsInReport() {
        const pages = await this.report.getPages();
        if (!pages?.length) {
            this.showReport = true;
            this.isLoaded = true;
            return;
        }

        const firstPage = pages[0];
        const visuals = await firstPage.getVisuals();

        const userExistVisual = visuals.find(
            (v: any) => v.title?.toLowerCase().trim() === 'userexists'
        );

        if (!userExistVisual) {
            this.showReport = true;
            this.isLoaded = true;
            return;
        }

        const dataView = await userExistVisual.exportData(
            powerbi.models.ExportDataType.Summarized
        );
        const csvData = dataView.data?.trim();
        const rows = csvData?.split('\n').filter(Boolean) || [];

        const value =
            rows.length > 1
                ? parseInt(
                      rows[1].split(',')[0].replace(/['"]+/g, '').trim(),
                      10
                  )
                : NaN;

        this.showNoUserContent = value === 0;
        this.showReport = value !== 0;
        this.isLoaded = true;
    }

    getDateSlicerFilter() {
        const today = new Date();
        const lastYear = new Date();
        lastYear.setFullYear(today.getFullYear() - 1);
        const startDate = lastYear.toISOString();
        const endDate = today.toISOString();
        let slicerConfig = {
            selector: {
                $schema:
                    'http://powerbi.com/product/schema#slicerTargetSelector',
                target: {
                    table: 'Date',
                    column: 'Date',
                },
            },
            state: {
                filters: [
                    {
                        $schema: 'http://powerbi.com/product/schema#advanced',
                        target: {
                            table: 'Date',
                            column: 'Date',
                        },
                        logicalOperator: 'And',
                        conditions: [
                            {
                                operator: 'GreaterThanOrEqual',
                                value: startDate,
                            },
                            {
                                operator: 'LessThanOrEqual',
                                value: endDate,
                            },
                        ],
                        filterType: models.FilterType.Advanced,
                    },
                ],
            },
        };
        return [slicerConfig];
    }

    getFilters() {
        const filters = [];
        const today = new Date();
        const lastYear = new Date();
        lastYear.setFullYear(today.getFullYear() - 1);

        // Convert to ISO strings (in UTC)
        const startDate = lastYear.toISOString();
        const endDate = today.toISOString();
        const dateFilter = {
            $schema: 'http://powerbi.com/product/schema#slicerTargetSelector',
            target: {
                table: 'Date',
                column: 'Date',
            },
            logicalOperator: 'And',
            conditions: [
                {
                    operator: 'LessThanOrEqual',
                    value: endDate,
                },
            ],
        };
        filters.push(dateFilter);
        if (this.user.userType === 'orgadmin') {
            this.reportTitle = COMMON_CONFIG.POWER_BI_REPORT_TYPES.clientReport;
        } else {
            if (this.orgId) {
                filters.push(
                    this.powerBIService.createFilter(
                        'view_organization',
                        'ID',
                        [this.orgId]
                    )
                );
                this.reportTitle =
                    COMMON_CONFIG.POWER_BI_REPORT_TYPES.organizationReport;
            } else {
                this.reportTitle =
                    COMMON_CONFIG.POWER_BI_REPORT_TYPES.adminReport;
            }
        }
        return filters;
    }

    async downloadReportAsPDF() {
        this.powerBIService.setExportState(true);
        try {
            const options = {
                personalizeVisuals: true,
                allPages: true,
            };
            const bookmarkState =
                await this.report.bookmarksManager.capture(options);
            this.powerBIService
                .getExportId(this.orgId, bookmarkState.state)
                .subscribe(
                    response => {
                        if (response) {
                            this.exportId = response.exportID;
                            this.pollForReportStatus();
                        }
                    },
                    error => {
                        console.error('Error fetching export ID:', error);
                    }
                );
        } catch (error) {
            console.error('Error during report export:', error);
            this.powerBIService.setExportState(false);
        }
    }

    pollForReportStatus() {
        const POLL_INTERVAL = 5000;

        const checkStatus = () => {
            this.powerBIService
                .checkReportGenerated(this.exportId, this.tokenObject, false)
                .then(exportResponse => {
                    if (exportResponse.status === 'Succeeded') {
                        this.downloadReport();
                    } else {
                        setTimeout(checkStatus, POLL_INTERVAL);
                    }
                })
                .catch(error => {
                    this.powerBIService.setExportState(false);
                });
        };

        checkStatus();
    }

    downloadReport() {
        this.powerBIService
            .checkReportGenerated(this.exportId, this.tokenObject, true)
            .then((file: Blob) => {
                // Trigger file download
                const link = document.createElement('a');
                const url = URL.createObjectURL(file);
                link.href = url;
                link.download =
                    this.reportTitle +
                    '_' +
                    moment().format('MM-DD-YYYY_HH.mm.ss') +
                    '.pdf';
                link.click();
                URL.revokeObjectURL(url);
                this.powerBIService.setExportState(false);
            })
            .catch(error => {
                this.powerBIService.setExportState(false);
            });
    }
}
